import { Checkin, ManualOverrideType, OverrideActor } from '../api/client'

/** Who owns a `checkin_features` / `checkin_projects` row. */
export type Owner = 'parser' | 'assistant' | 'user'

/**
 * The frontend twin of the backend's `owner_of`. No override is the parser's;
 * an override is Genie's when `override_actor` says so and the user's
 * otherwise, including when the actor is absent — so a row from an older
 * payload errs toward "owned".
 */
export function ownerOf(row: {
    manual_override?: ManualOverrideType | string | null
    override_actor?: OverrideActor | null
}): Owner {
    if (row.manual_override == null) return 'parser'
    return row.override_actor === 'assistant' ? 'assistant' : 'user'
}

/**
 * True when a feature should be considered "live" on a checkin — i.e. it has
 * no rows yet, or at least one of its rows is not DELETE-tombstoned.
 *
 * List-typed features can have multiple `checkin_features` rows for the same
 * feature_id (one per list value), and a row-replace cycle can leave a mix of
 * DELETE tombstones and `keep_value` rows side by side. A naive
 * `.find(...).manual_override !== DELETE` check picks an arbitrary row and
 * may report the feature as deleted even when other rows are live — that
 * misbehavior was the original motivation for extracting this helper.
 */
export function hasActiveCheckinFeatureRow(
    checkin: Pick<Checkin, 'checkin_features'>,
    featureId: number,
): boolean {
    const rows = checkin.checkin_features.filter((cf) => cf.feature_id === featureId)
    if (rows.length === 0) return true
    return rows.some((cf) => cf.manual_override !== ManualOverrideType.DELETE)
}

/**
 * Who owns a list/map feature on this checkin, in the sense
 * `docs/list_tagging.md` uses: *any* row of the feature the parser does not
 * own locks the whole feature, so a re-parse skips it and a stray emission for
 * it is dropped. Anything but `parser` is locked; which of the other two it is
 * only changes the padlock's hover text. The user comes ahead of Genie should
 * the two ever be mixed.
 *
 * Both shapes of the lock count, which is why the question is asked this way
 * rather than by testing for `KEEP_VALUE`:
 *
 *   - live rows stamped `keep_value` — typed, picked or pinned;
 *   - the cleared sentinel, a lone value-less `delete` row — the list was
 *     emptied, and the row exists to hold the lock.
 *
 * A list locked by a tombstone is as locked as one locked by a kept value.
 */
export function checkinFeatureOwner(
    checkin: Pick<Checkin, 'checkin_features'>,
    featureId: number,
): Owner {
    let owner: Owner = 'parser'
    for (const cf of checkin.checkin_features) {
        if (cf.feature_id !== featureId) continue
        const rowOwner = ownerOf(cf)
        if (rowOwner === 'user') return 'user'
        if (rowOwner === 'assistant') owner = 'assistant'
    }
    return owner
}
